import type { SnackKey } from 'config/notifications'

import ConfirmationDialogExclude from 'components/atoms/ConfirmationDialogExclude'
import ModalWindow from 'components/molecules/ModalWindow'
import { CONSTS } from 'config/objectConst'
import { campaignApi, campaignHooks } from 'features/campaigns'
import { executionsApi } from 'features/executions'
import { notificationHooks } from 'features/notifications'
import { useSwitcher } from 'shared/hooks'
import { useConfirmState } from 'shared/odata/confirm'
import { type ContactsTable, LoadingButton } from 'shared/ui'

const Icon = (
	<svg
		aria-hidden
		fill='currentcolor'
		height={16}
		viewBox='0 0 25 25'
		width={16}
	>
		<path
			clipRule='evenodd'
			d='M12.184 2.04C6.576 2.04 2.03 6.586 2.03 12.194s4.546 10.154 10.154 10.154c5.607 0 10.153-4.546 10.153-10.154S17.791 2.04 12.184 2.04zm-12 10.154c0-6.628 5.372-12 12-12 6.627 0 12 5.372 12 12 0 6.627-5.373 12-12 12-6.628 0-12-5.373-12-12zm17.538-.923v1.846H6.645V11.27h11.077z'
			fillRule='evenodd'
		/>
	</svg>
)

export function Exclude({
	amount = 0,
	filters: { $filter, $orderby, $searchQuery },
	unSelectAll
}: Pick<
	ContactsTable.Types.ActionsProps,
	'amount' | 'filters' | 'unSelectAll'
>) {
	const { campaign, isSuccess } = campaignHooks.useCurrent()

	const modalSwitcher = useSwitcher()

	const snack = notificationHooks.useSnack()

	const [deleteExecutions, deleteExecutionsOptions] =
		executionsApi.useDeleteExecutionsMutation()
	const [deleteFromCampaign, deleteFromCampaignOptions] =
		campaignApi.useAudienceDeleteMutation()
	const [checkPreview, checkPreviewOptions] =
		executionsApi.useLazyPreviewExecutionsDeleteQuery()

	const isCompose = campaign?.state === 'Compose',
		isRunning = !isCompose

	const modalData = CONSTS.confirmationDialogs[isCompose ? 1.6 : 1.8]

	const chosenFilters = { $filter, $orderby, $searchQuery }

	const confirmState = useConfirmState()

	async function deleteRunningExecutions() {
		const { data } = await deleteExecutions(chosenFilters)

		if (data !== undefined) {
			snack('3.16')
			unSelectAll()
		}
	}

	async function handleButtonClick() {
		if (isCompose) {
			return modalSwitcher.on()
		}

		const { data } = await checkPreview(chosenFilters)

		if (!data) {
			return
		}

		const isValid =
			data.validation_rules_summary.length === 0 &&
			data.valid_audience_count > 0

		if (isValid) {
			return modalSwitcher.on()
		}

		confirmState.set({
			data,
			id: campaign?.id ?? '',
			status: true
		})
	}

	async function handleModalConfirm() {
		modalSwitcher.off()
		if (isRunning) {
			return deleteRunningExecutions()
		}

		const key: SnackKey = amount > 1 ? '3.5b' : '3.5a'

		const { data } = await deleteFromCampaign({
			...chosenFilters,
			id: campaign.id ?? ''
		})

		if (data) {
			snack(key)
			return unSelectAll()
		}
	}

	return (
		<>
			<LoadingButton
				disabled={!isSuccess}
				isLoading={
					deleteExecutionsOptions.isLoading ||
					deleteFromCampaignOptions.isLoading ||
					checkPreviewOptions.isLoading
				}
				onClick={handleButtonClick}
				variant='danger'
			>
				{Icon}
				Exclude
			</LoadingButton>

			<ModalWindow
				closeHandler={modalSwitcher.off}
				closeText='No'
				confirmHandler={handleModalConfirm}
				confirmText='Yes'
				open={modalSwitcher.isOn}
				{...modalData}
			/>

			{isRunning && (
				<ModalWindow
					classes='confirmation-launch-modal'
					closeHandler={confirmState.reset}
					open={confirmState.state.status}
				>
					<ConfirmationDialogExclude
						closeHandler={confirmState.reset}
						confirmationDialog={confirmState.state}
						exclude={deleteRunningExecutions}
					/>
				</ModalWindow>
			)}
		</>
	)
}
